import { Loader, useModal } from '@jutro/components';
import { BreakpointTrackerContext } from '@jutro/layout';
import { useAuthentication } from '@xengage/gw-digital-auth-react';
import { messages as commonMessages } from '@xengage/gw-platform-translations';
import { useValidation } from '@xengage/gw-portals-validation-react';
import { useDataRefresh, ViewModelForm, ViewModelServiceContext } from '@xengage/gw-portals-viewmodel-react';
import _ from 'lodash';
import React, { useCallback, useContext, useState, useRef, useEffect } from 'react';
import ContactService from '../../../../../capabilities/twia-capability-quoteandbind-residential/services/ContactService.js';
import TWIAAdditionalInsuredDetails from '../TWIAAdditionalInsuredDetails/TWIAAdditionalInsuredDetails.jsx';
import messages from './TWIAAdditionalInsuredComponent.messages.js';
import metadata from './TWIAAdditionalInsuredComponent.metadata.json5';
import styles from '../TWIAAdditionalInterestComponent.module.scss';


import { Button } from '@jutro/legacy/components';


function TWIAAdditionalInsuredComponent(props) {
    const breakpoint = useContext(BreakpointTrackerContext);
    const { submissionVM, updateWizardData, showErrors, findInvalidComponent, onValidate } = props;
    const { authHeader } = useAuthentication();
    const [isLoading, setIsLoading] = useState(false); // DE18345
    const viewModelService = useContext(ViewModelServiceContext);
    const [additionalInfoVM, setAdditionalInfoVM] = useState();
    const [showAdditionalInsured, setShowAdditionalInsured] = useState(false);
    const { showAlert, showConfirm } = useModal();
    const policyContactDetailsDTO = 'edge.capabilities.gateway.contact.dto.PolicyContactDetailsDTO';
    const { refreshData } = useDataRefresh();
    const [additionalInsured, setAdditionalInsured] = useState([]);
    const additionalInsuredData = useRef(additionalInsured);

    useEffect(() => {
        additionalInsuredData.current = _.filter(submissionVM.baseData.policyContactDetails_TWIA?.value,
            pcd => pcd.policyContactRoles[0].subtype === 'PolicyAddlNamedInsured');
    })

    const addAdditionalInsured = useCallback(() => {

        const newContact = viewModelService.create({}, 'pc', 'edge.capabilities.gateway.contact.dto.PolicyContactDetailsDTO')

        newContact.active = true;
        newContact.contactType_TWIA.value = viewModelService.productMetadata.get('pc').types.getTypelist('typekey.ContactType').getCode('person');


        const allContacts = submissionVM.baseData.policyContactDetails_TWIA;
        const primaryNamedInsured = allContacts.children.find(c => c.policyContactRoles.value.some(pcr => pcr.subtype === 'PolicyPriNamedInsured'));

        newContact.primaryAddress.value = primaryNamedInsured.primaryAddress.value
        newContact.subtype.value = 'Person';

        const pcr = { subtype: 'PolicyAddlNamedInsured' };

        pcr.accountContactRole = { subtype: 'NamedInsured' };
        pcr.policyAdditionalNamedInsured = {};
        newContact.policyContactRoles.value = [pcr];
        setAdditionalInfoVM(() => newContact)
        setShowAdditionalInsured(true)
        // OnNext will not call again if there are no changes to the model so setting dummy tag.
        _.set(submissionVM, 'isEditingAdditionalInsured', true)
        updateWizardData(submissionVM)
    }, [])

    const oldVMData = [];
    const saveAdditionalInsureds = async (updatedData) => {
        const newItem = [...additionalInsuredData.current, updatedData.value];

        await saveAdditionalInterest()
        updateWizardData(submissionVM);
        setShowAdditionalInsured(false);
    };

    const cancelAdditionalInsureds = () => {
        setAdditionalInfoVM(oldVMData);
        setShowAdditionalInsured(false);
    };

    const getJobID = (job) => job.quoteID ? job.quoteID.value : job.jobID.value

    const saveAdditionalInterest = async () => {
        setIsLoading(true);

        await ContactService.createPolicyContact(getJobID(submissionVM), additionalInfoVM.value, authHeader).then((newContact) => {

            if (newContact) {
                const contactDetailsDTO = viewModelService.create(newContact, 'pc', policyContactDetailsDTO);
                const policyContactDetails = submissionVM.baseData.policyContactDetails_TWIA;

                if (policyContactDetails.value == null) {

                    policyContactDetails.value = {};
                }

                policyContactDetails.value.push(contactDetailsDTO.value);
                additionalInsuredData.current.push(contactDetailsDTO.value)
                updateWizardData(submissionVM)
                setIsLoading(false);
            }
        })
            .catch(() => {
                showAlert({
                    title: messages.saveFailedTitle,
                    message: messages.saveFailedMsg,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            });

    }


    const deleteAdditionalInterest = async (aiPublicID) => {
        setIsLoading(true);

        await ContactService.removePolicyContact(getJobID(submissionVM), aiPublicID, authHeader).then(() => {

            const policyContactDetails = submissionVM.baseData.policyContactDetails_TWIA;

            if (policyContactDetails.value != null) {

                policyContactDetails.value = _.filter(policyContactDetails.value, pcd => pcd.publicID !== aiPublicID);
            }

            setIsLoading(false);
        })
            .catch(() => {
                showAlert({
                    title: messages.saveFailedTitle,
                    message: messages.saveFailedMsg,
                    status: 'error',
                    icon: 'mi-error-outline',
                    confirmButtonText: commonMessages.ok,
                }).catch(_.noop);
            });

    }

    const deleteRow = async (item) => {
        showConfirm({
            title: messages.removeAdditionalInsured,
            message: messages.removeAddlInsuredMessage,
            status: 'warning',
            icon: 'mi-error-outline',
            confirmButtonText: commonMessages.ok,
            cancelButtonText: messages.cancel,
            className: styles.jut__Button__filled,
        }).then(async (results) => {
            setIsLoading(true);

            if (results === 'cancel' || results === 'close') {
                return _.noop();
            }

            const aiPublicID = item.publicID;

            await deleteAdditionalInterest(aiPublicID);

            additionalInsuredData.current = additionalInsuredData.current.filter((ai) => ai.publicID !== aiPublicID);
            setAdditionalInsured(additionalInsuredData.current)
            updateWizardData(submissionVM);
            setIsLoading(false);

            return true;
        }, _.noop)
    };

    const getName = (item) => item.subtype === 'Person' ? item.firstName + " " + item.lastName : item.contactName;

    const getRelationship = (item) => item.policyContactRoles[0].policyAdditionalNamedInsured?.relationshipToPrimaryNamedInsured;

    const actionButtons = (item, index) => (
        <div style={{ display: 'flex', justifyContent: 'center' }}>
            <Button
                className={styles.buttons}
                type="text"
                icon="gw-delete"
                onClick={() => deleteRow(item, index)}
                aria-label="Delete Row"
            />
        </div>
    );

    const overrideProps = {
        '@field': {
            labelPosition: breakpoint === 'desktop' ? 'left' : 'top',
        },
        add: {
            visible: !showAdditionalInsured
        },
        additionalInsuredSection: {
            visible: showAdditionalInsured,
            item: additionalInfoVM,
            saveData: saveAdditionalInsureds,
            cancelData: cancelAdditionalInsureds,
            isInvalid: findInvalidComponent,
            showErrors,
            onValidate
        },
        additionalInterestTable: {
            data: additionalInsuredData.current
        },

    };

    const writeValue = useCallback(
        (value, path) => {
            _.set(submissionVM, `${path}.value`, value);
            updateWizardData(submissionVM);
            refreshData();
        },
        [submissionVM, refreshData, updateWizardData]
    );

    const resolvers = {
        resolveClassNameMap: styles,
        resolveComponentMap: { addtionalInsuredDetailsComponent: TWIAAdditionalInsuredDetails },
        resolveCallbackMap: {
            onAdd: addAdditionalInsured,
            getName,
            getRelationship,
            actionButtons
        },
    };


    return (
        <Loader loaded={!isLoading}>
        <ViewModelForm
            uiProps={metadata.pageContent}
            model={submissionVM}
            overrideProps={overrideProps}
            onModelChange={updateWizardData}
            callbackMap={resolvers.resolveCallbackMap}
            componentMap={resolvers.resolveComponentMap}
            classNameMap={resolvers.resolveClassNameMap}
            onValueChange={writeValue}
        />
    </Loader>
    );
}

export default TWIAAdditionalInsuredComponent;
